import { Children, cloneElement, createElement } from 'react';
/**
 * Include layout css class and allows overriding of a core component with custom component.
 *
 * @param {any} coreComponent - The core component to wrap.
 * @param {string} className - The class name to apply to the wrapped component.
 * @returns {CoreComponent} A core component with css class layout appended
 */
const allowOverride = (coreComponent, className) => {
  const OverrideWrapper = _ref => {
    let {
      children,
      override,
      customGrid
    } = _ref;
    if (override && children) {
      return /*#__PURE__*/cloneElement(children, {
        className
      });
    }
    const propsWithChildren = {
      children,
      customGrid,
      className
    };
    return /*#__PURE__*/createElement(coreComponent, {
      ...propsWithChildren
    });
  };
  return OverrideWrapper;
};

/**
 * Checks if the given child is a React element of the specified core component type.
 *
 * @param {CoreComponent} coreComponent - The core component type to check against.
 * @param {ReactNode} child - The child node to be checked.
 * @returns {boolean} - Returns true if the child is a React element of the specified core component type, otherwise false.
 */
const isChildCoreComponent = (coreComponent, child) => {
  const isReactElement = node => {
    return typeof node === 'object' && node !== null && 'type' in node;
  };
  if (child && isReactElement(child)) {
    return child.type === coreComponent;
  }
  return false;
};

/**
 * Check the children for components that are in the core components list
 *  - The core components that are NOT found in the list are included as components to render.
 *
 * @param {CoreComponent[]} coreComponents - An array of core components.
 * @param {ReactNode} [children] - The child elements to be rendered.
 * @returns {[ReactElement[], ReactNode[]]} An array containing the components to render and the remaining children.
 */
const renderCoreComponents = (coreComponents, children) => {
  const componentsToRender = [];
  const childrenArray = Children.toArray(children);
  coreComponents.forEach((component, index) => {
    const coreComponentIndex = childrenArray.findIndex(child => {
      return isChildCoreComponent(component, child);
    });
    if (coreComponentIndex === -1) {
      componentsToRender.push(/*#__PURE__*/createElement(component, {
        key: index
      }));
    } else {
      const coreComponentFromChild = childrenArray.splice(coreComponentIndex, 1)[0];
      componentsToRender.push(/*#__PURE__*/cloneElement(coreComponentFromChild, {
        key: index
      }));
    }
  });
  return [componentsToRender, childrenArray];
};

/**
 * Extracts and sorts the values of specified keys from a given component object.
 * If no keys are provided, it returns all values from the component object.
 *
 * @template T - The type of the component object.
 * @template K - The keys of the component object.
 *
 * @param {T} component - The component object from which values are to be extracted.
 * @param {K[]} [keys] - An optional array of keys whose corresponding values need to be extracted.
 *
 * @returns {Array<CoreComponent>} An array of values corresponding to the specified keys in the order of the keys list or all values if no keys are provided.
 */
const sortCoreComponents = (component, keys) => {
  return keys ? keys.map(key => component[key]) : Object.values(component).slice(2);
};
const firstToLowerCase = name => name.charAt(0).toLowerCase() + name.slice(1);

/**
 * Appends a layout css class to each component in the core components object and the option to override
 *
 * @template T - The type of the core components object.
 * @param {T} coreComponents - The core components object whose components will have layout css classes appended.
 * @returns {CoreComponentType<T>} A new object with layout classes appended to each component.
 */
const appendLayoutClass = coreComponents => Object.entries(coreComponents).reduce((acc, _ref2) => {
  let [key, value] = _ref2;
  const className = firstToLowerCase(key);
  acc[key] = allowOverride(value, className);
  return acc;
}, {});

/**
 * returns a component with compound static members
 * This means that all components in coreComponents will be possible to access by:
 * MasterComponent.CoreComponentName
 *
 * @template T - The type of the master component.
 * @template K - The type of the core components.
 * @param {T} masterComponent - component that will have other components assigned on
 * @param {CoreComponentType<K>} coreComponents - Object with components to be assigned
 * @returns {T & {CoreComponents<K>}} component with static members
 */
const bindCoreComponents = (masterComponent, coreComponents) => Object.assign(masterComponent, appendLayoutClass(coreComponents));
export { renderCoreComponents, sortCoreComponents, bindCoreComponents, allowOverride, firstToLowerCase, appendLayoutClass, isChildCoreComponent };
//# sourceMappingURL=product-layout-utils.js.map